import React, { FunctionComponent } from 'react'
import { useSelector } from 'react-redux'
import { Link as ReactLink, useLocation } from 'react-router-dom'
import { Center, Link, MenuList, MenuItem } from '@chakra-ui/react'
import { capabilities } from '@homevest/utils'

import {
  RETOOL_APP_URL,
  RETOOL_CHART_OF_ACCOUNTS_EDITOR_APP_ID,
  RETOOL_DISPOSITIONS_TOOL_APP_ID,
  RETOOL_PAYMENT_ALLOCATIONS_MANAGER_APP_ID
} from 'constants/urls'
import { canAccessVacancyReadiness, hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'

import TabMenu from './TabMenu'
import TabLink from './TabLinkWrapper'

const { APPLICATION_VIEWER, DAGGER, PERMISSION_EDITOR, LEDGER_VIEWER } =
  capabilities.CAPABILITY_TYPES

const NavigationTabs: FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const { pathname } = useLocation()

  const admin = useSelector<StoreState, any>((store) => store.admin)
  const isDagger = hasCapability(admin, DAGGER)
  const isRentalAppViewer = hasCapability(admin, APPLICATION_VIEWER)
  const isPermissionEditor = hasCapability(admin, PERMISSION_EDITOR)
  const isLedgerViewer = hasCapability(admin, LEDGER_VIEWER)
  const today = new Date()
  const isApril1st = today.getDate() === 1 && today.getMonth() === 3

  return (
    <>
      <TabMenu
        label='Properties'
        active={
          pathname.match('/homes|/properties|/rent-roll|/evictions|/vacancy-readiness') !== null
        }
      >
        <MenuList fontSize='sm'>
          <MenuItem as={ReactLink} to='/homes'>
            Homes
          </MenuItem>
          {isLedgerViewer && (
            <MenuItem as={ReactLink} to='/rent-roll'>
              Rent Roll
            </MenuItem>
          )}
          {canAccessVacancyReadiness(admin) && (
            <MenuItem as={ReactLink} to='/vacancy-readiness'>
              Vacancy Manager
            </MenuItem>
          )}
          <MenuItem as={ReactLink} to='/properties/renewal-manager'>
            Renewal Manager
          </MenuItem>
          <MenuItem as={ReactLink} to='/collections'>
            Collections
          </MenuItem>
          <MenuItem as={ReactLink} to='/evictions'>
            Evictions
          </MenuItem>
          <MenuItem as={ReactLink} to='/properties'>
            Search Listings
          </MenuItem>
          <MenuItem
            as={Link}
            href={`${RETOOL_APP_URL}${RETOOL_DISPOSITIONS_TOOL_APP_ID}`}
            target='_blank'
          >
            Dispositions Tracker
          </MenuItem>
        </MenuList>
      </TabMenu>
      <TabMenu label='Customers' active={pathname.match('/users') !== null}>
        <MenuList fontSize='sm'>
          <MenuItem as={ReactLink} to='/users'>
            Search
          </MenuItem>
          <MenuItem as={ReactLink} to='/users/create'>
            Create
          </MenuItem>
        </MenuList>
      </TabMenu>

      <TabMenu
        label='Applications'
        active={pathname.match('/applications|/review|/dispatch-lead-groups') !== null}
      >
        <MenuList fontSize='sm'>
          {isRentalAppViewer && (
            <MenuItem as={ReactLink} to='/applications'>
              View All
            </MenuItem>
          )}
          <MenuItem as={ReactLink} to='/dispatch-lead-groups'>
            Lead Groups
          </MenuItem>
          <MenuItem as={ReactLink} to='/review'>
            Pending Move-In Payments
          </MenuItem>
        </MenuList>
      </TabMenu>
      <TabMenu label='Financials' active={pathname.match('/statements') !== null}>
        <MenuList fontSize='sm'>
          <MenuItem
            as={Link}
            href={`${RETOOL_APP_URL}${RETOOL_CHART_OF_ACCOUNTS_EDITOR_APP_ID}`}
            target='_blank'
          >
            Chart of Accounts Editor
          </MenuItem>
          <MenuItem
            as={Link}
            href={`${RETOOL_APP_URL}${RETOOL_PAYMENT_ALLOCATIONS_MANAGER_APP_ID}`}
            target='_blank'
          >
            Payment Allocations Manager
          </MenuItem>
          <MenuItem as={ReactLink} to='/buildium'>
            Buildium
          </MenuItem>
          {isDagger && (
            <>
              <MenuItem as={ReactLink} to='/statements/bulk-je-uploader'>
                Bulk Journal Entry Uploader
              </MenuItem>
              <MenuItem as={ReactLink} to='/statements/fmv'>
                FMV Marks
              </MenuItem>
              <MenuItem as={ReactLink} to='/statements/mortgage-transformer'>
                Mortgage Transformer
              </MenuItem>
              <MenuItem as={ReactLink} to='/statements/ship'>
                Ship Statements
              </MenuItem>
            </>
          )}
        </MenuList>
      </TabMenu>

      <TabMenu label='Resources' active={pathname.match('/resources') !== null}>
        <MenuList fontSize='sm'>
          <MenuItem as={ReactLink} to='/dashboards'>
            Dashboards
          </MenuItem>
          <MenuItem as={ReactLink} to='/documents'>
            Documents
          </MenuItem>
        </MenuList>
      </TabMenu>

      {isPermissionEditor && (
        <TabLink active={pathname.match('/capabilities') !== null}>
          <Link as={ReactLink} to='/capabilities' h='full' px={2}>
            <Center h='full'>Permissions</Center>
          </Link>
        </TabLink>
      )}

      {isApril1st && (
        <TabLink active={pathname.match('/lineage') !== null}>
          <Link as={ReactLink} to='/lineage' h='full' px={2}>
            <Center h='full'>Lineage</Center>
          </Link>
        </TabLink>
      )}
    </>
  )
}

export default NavigationTabs
